/* ============================================================
   AEPERION — Page transition veil

   ------------------------------------------------------------
   ADD TO A PAGE — one line, in the page's existing entry module:

       import { initTransitions } from '@aep/transition';   // then:
       initTransitions();

   That is the whole integration. The stylesheet is imported below, so
   Vite links it into the page bundle automatically; no <link> tag and
   no markup are required, and none of the four page modules need to
   know anything else. The veil builds its own DOM on first use.

   On Shopify, the CSS is linked the theme's way instead. Delete the
   `import '../styles/transition.css'` line below — it is the one thing
   in this file that assumes a bundler — and add:

       {{ 'transition.css' | asset_url | stylesheet_tag }}

   Everything else ports as-is alongside motion.js.

   ------------------------------------------------------------
   WHAT IT DOES

   A rack of mark-angled facets rises from below and seals into a flat
   --ink-000 field; the mark assembles from its own two limbs while the
   page loads; on the next page the same rack keeps going, up and out.
   One continuous ascent through the black.

   ------------------------------------------------------------
   THE FOUR THINGS THIS FILE IS DEFENSIVE ABOUT

   1. It never gates content. The veil is an element ADDED over pixels
      that are already painted, and it is REMOVED again. Nothing waits
      on it. If this module throws on line one, every link on the site
      still works and every page still renders — you just get no
      transition. That is the specified failure mode, and it is the
      opposite of live aeperion.com, where a dark-mode app sets
      `body { opacity: 0 }` until 182 KB of third-party JS lands.

   2. It never covers content the user has already read. On arrival the
      veil mounts ONLY if first-contentful-paint has not happened yet
      (see contentPainted). If we are late, we skip the entrance
      entirely rather than black out a page someone is looking at.

   3. It never traps. A timer is armed BEFORE any animation starts and
      clears the veil unconditionally; popstate, bfcache restore and a
      catch-all around the exit all hard-clear as well. Modifier
      clicks, target=_blank, download, rel=external, cross-origin and
      non-http links are never intercepted at all — hrefs are left
      alone, so the no-JS behaviour is the native behaviour.

   4. It does not invent latency. Navigation commits the instant the
      field seals (~400ms, capped at 520ms); the mark animation plays
      over the network wait, not before it. Hovering a link warms it
      with rel=prefetch, so on a warm cache the seal IS the wait.

   ------------------------------------------------------------
   ⚠ INTERACTION WITH THE THEME'S CROSS-DOCUMENT VIEW TRANSITIONS

   Shopify Tinker (and Horizon, which it descends from) ships
   `@view-transition { navigation: auto }` plus a #MainContent render
   blocker, and names main-content / product-details / product-grid /
   sticky-header / footer.

   An overlay with an exit animation and a cross-document transition are
   mutually exclusive on the same click, and it is worth being precise
   about why. A CDVT snapshots the outgoing document at `pageswap` and
   then stops painting it: ::view-transition-old() is a static bitmap.
   An animation that starts on click would be frozen into that bitmap at
   whatever frame it reached. To animate before leaving we must
   preventDefault and navigate ourselves — and a script-initiated
   location.assign() is reported not to qualify for a CDVT at all. So on
   the links we handle, expect to REPLACE the theme's transition rather
   than compose with it.

   That is a deliberate trade. The theme's transition is a 125ms
   cross-fade; the brief is a mark that assembles.

   The one thing we cannot do is outrank it. Per css-view-transitions-1
   the ::view-transition layer "paints after all other content of the
   document (including any content rendered in the top layer)", so no
   z-index, no position:fixed and no <dialog> can lift a DOM overlay
   above a running one. The veil has to join the transition instead of
   fighting it, which is exactly what the view-transition-name in
   transition.css does. Without that name the theme's main-content group
   paints page content straight over our black field.

   We stay cooperative:
     · Links we decline (external, downloads, modified clicks, reduced
       motion, low-end devices) are untouched, so the theme's own
       transition still runs on them, with its own reduced-motion and
       low-power bail-outs intact.
     · The veil is named and its group raised, so if a CDVT does fire
       around our navigation, the field stays on top.
     · If it misbehaves, `initTransitions({ viewTransitions: 'replace' })`
       opts the document out at runtime, the same way Shopify's own
       view-transitions.js does for Android WebViews.

   VERIFY ON THE REAL THEME. It is the one claim in this file that a
   Vite dev server cannot test.

   Support, for scale (July 2026): cross-document VT is Chrome 126+ and
   Safari 18.2+, ~82% global — Firefox has NOT shipped it (meta bug
   1860854 still open; it is an Interop 2026 focus area). Firefox users
   get this veil and nothing else, which is the whole argument for not
   building the transition out of view transitions in the first place.

   Two more notes from Shopify's assets/view-transitions.js, which reads
   as a field guide to what breaks:
     · They skip when hardwareConcurrency <= 2 or deviceMemory <= 2. So
       do we (lowEnd).
     · They disable CDVT outright in Android in-app WebViews (Facebook,
       Instagram, TikTok), which "can freeze or white-screen the
       storefront on navigation" — their words, June 2026 testing. That
       bug is in the browser's transition machinery; this veil is
       ordinary transform animation and is not exposed to it. It is
       still the reason not to add a CDVT of our own on top.

   And one API note: `pagereveal` cannot be used from here. Its listener
   must be registered in a parser-blocking <script> in <head>; a deferred
   module has already missed it. `pageswap` on the outgoing page is fine,
   and that is the only one we use.
   ============================================================ */

import { EASE_CSS, reduced } from '@aep/motion';
/* stylesheet moved to Liquid: transition */
/* ============================================================
   The animation engine: WAAPI. Formerly GSAP.

   This file used gsap.timeline() + to/fromTo/set/killTweensOf, and it was
   the last consumer of GSAP core on the site. Measured: GSAP is 49.8 kB
   gzip and 87–88% of every page's JavaScript; the whole of what it was
   doing here is expressible in Element.animate, which is 94.1% global and
   the same audience GSAP itself supports.

   The four translations that are not one-for-one, so they are worth
   stating rather than leaving to be rediscovered:

     · `stagger: { each, from: 'center' }` → CENTRE_DELAY below. GSAP uses
       fractional distance for even counts; the rack is 9 or 5, always odd,
       so integer distance is exact.
     · `gsap.timeline()` + absolute offsets → a `delay` per animation. There
       is no shared clock, which is why `t0` is recorded by hand.
     · `master.time()` → `performance.now() - t0`. Strictly more robust: it
       survives the animations being cancelled, which is precisely the state
       `onPageSwap` reads it in.
     · `yoyo: true, repeat: -1` → `direction: 'alternate', iterations: Infinity`.

   Every animation here is `fill: 'forwards'`, never `'both'`. Backwards fill
   would apply an animation's OPENING value during its delay, and this
   choreography is a chain of delayed animations over elements whose opening
   state is already written by primeMark()/seatMark(). With `'both'` the
   breath at 1.45s would slam the mark to its dimmed value at t=0 and erase
   the assembly that is supposed to precede it.
   ============================================================ */

/** Every animation the veil currently owns, so it can all be cancelled. */
let anims = [];

/** Master-timeline zero, in performance.now() terms. */
let t0 = 0;

/** ms — the tuning block below is in seconds, WAAPI wants milliseconds. */
const ms = (s) => Math.round(s * 1000);

/**
 * GSAP's `stagger: { each, from: 'center' }`, in one expression.
 * Distance from the middle index, so the centre shard leads and the two
 * outermost arrive together. Exact for odd counts; the rack is 9 or 5.
 */
const CENTRE_DELAY = (i, n, each) => Math.round(Math.abs(i - (n - 1) / 2)) * each;

/** el.animate(), with the house ease and forwards fill, tracked for teardown. */
function anim(el, keyframes, opts) {
  const a = el.animate(keyframes, {
    easing: EASE_CSS,
    fill: 'forwards',
    ...opts,
  });
  anims.push(a);
  return a;
}

/** gsap.set() — an immediate, inline write. */
const setStyle = (el, styles) => Object.assign(el.style, styles);

/**
 * gsap.killTweensOf() + timeline.kill(), natively.
 * Cancelling (not finishing) is deliberate: a forwards-filled animation
 * outranks inline style, so anything still filling would silently defeat
 * the primeMark()/seatMark() writes that come next.
 */
function killAll() {
  for (const a of anims) { try { a.cancel(); } catch { /* already gone */ } }
  anims = [];
}

/* ============================================================
   Tuning
   ============================================================ */

/** Seconds. Read this block as the choreography; nothing else has timing in it. */
const T = {
  seal: 0.30,          // one shard's rise
  sealStagger: 0.026,  // centre-out. 9 shards => 4 steps => seals at ~0.40s
  markIn: 0.30,        // the centre shard has just landed; the mark's ground is sealed
  draw: 0.40,          // outline traced from the apex, clockwise, back to the apex
  facet: 0.60,         // the two limbs converging
  breathAt: 1.45,      // only reached if the network is genuinely slow
  breath: 2.30,
  release: 0.18,       // the mark letting go, on arrival
  clear: 0.34,         // one shard leaving through the top
  clearStagger: 0.022,
};

/** When the mark has finished assembling, in master-timeline seconds. Derived,
 *  not typed, so retiming T above cannot silently desync the handoff below. */
const MARK_DONE = T.markIn + 0.06 + 0.05 + T.facet;

/** Below this much of the assembly, the outgoing page never really showed a
 *  mark, so the arriving page does not pretend there was one. */
const MARK_CARRY = 0.55;

const DEFAULTS = {
  /** Shards across. 'auto' = 9 wide / 5 narrow, matching the shell's 44em break. */
  shards: 'auto',
  /** Warm same-origin destinations on hover/focus. Skipped if the theme already ships speculation rules. */
  prefetch: true,
  /** Anything matching this, or inside it, navigates natively. */
  ignore: '[data-no-transition]',
  /**
   * 'cooperate' — leave the theme's `@view-transition` alone. Links we
   *   decline keep their native transition, and the named veil in
   *   transition.css keeps ours on top if one fires around our navigation.
   * 'replace'  — opt this document out of cross-document view transitions
   *   entirely, by injecting `@view-transition { navigation: none }`. This
   *   is the mechanism Shopify's own view-transitions.js uses to disable
   *   itself; the cascade takes the LAST matching rule, so a late
   *   stylesheet wins. Reach for it only if you have measured a conflict.
   */
  viewTransitions: 'cooperate',
  /** Hard cap on added latency: commit even if the rack has not finished. */
  maxSealMs: 520,
  /** Unconditional release. If navigation stalls or dies, the page comes back. */
  safetyMs: 6000,
  /** How long an exit stamp stays valid for the next page to claim. */
  flagTtlMs: 4000,
};

const FLAG = 'aep-tx';
const FLAG_MARK = 'aep-tx-mark';

/* ------------------------------------------------------------
   THE MARK

   This is the Aeperion mark from the brand's VECTOR MASTER — the same two
   subpaths, character for character, as snippets/aep-logo.liquid's
   .logo-mark and assets/aep-mark.svg. It did not used to be: the veil
   carried the earlier hand-traced approximation (the 717 × 743 single
   outline that still lives in src/partials/shell.html), so the loading
   animation was assembling a mark the header no longer draws. The client
   flagged it on 1 Aug; this is the correction.

   HOW IT COMES APART
   The real mark IS two pieces — the short left blade and the long
   crossing blade — drawn as two closed subpaths. Each limb takes one
   subpath verbatim, so the halves cannot drift out of register with the
   mark or with each other: seated together they are exactly the artwork,
   with no clipping rectangles to keep in sync and no seam to overlap,
   because the subpaths never shared an edge in the first place.

   There is no separate inner facet, same as before: anything drawn inside
   the silhouette is the same ink on the same ink, and anything outside it
   is not the logo. The sustain breathes the whole mark.
   ------------------------------------------------------------ */

/** The two subpaths, verbatim from snippets/aep-logo.liquid — greppable
 *  against it and against assets/aep-mark.svg's geometry. */
const MARK_D_L = 'M 90.74 69.29 L 95.18 17.26 L 86.3 0 L 0 177.29 L 40.93 139.07 Z';
const MARK_D_R = 'M 129.57 88.64 L 0 177.28 L 16.03 181.17 L 108.93 136.41 L 134.63 101.65 L 114.75 141.59 L 163.11 181.17 L 172.36 177.28 Z';
const MARK_D = `${MARK_D_L} ${MARK_D_R}`;

const MARK = `
<svg class="aep-veil__mark" viewBox="0 0 172.36 181.17" aria-hidden="true" focusable="false" overflow="visible">
  <g class="aep-veil__limb" data-limb="l">
    <path fill-rule="nonzero" d="${MARK_D_L}"/>
  </g>
  <g class="aep-veil__limb" data-limb="r">
    <path fill-rule="nonzero" d="${MARK_D_R}"/>
  </g>
  <path class="aep-veil__outline" fill-rule="nonzero" d="${MARK_D}"/>
</svg>`;

/** Perimeter of the outline in user units — both subpaths' segments plus
 *  their closes, summed offline (410.54 + 535.40), so a browser that
 *  returns 0 from getTotalLength on an unrendered path still draws the
 *  trace. */
const MARK_LENGTH = 945.94;

/** Unit vector along the mark's longest edge: the left blade's ascent
 *  (86.3, 0) → (0, 177.29), normalised. The limbs converge along the
 *  mark's own ascent, never along a screen axis. */
const AXIS = { x: 0.438, y: 0.899 };

/** User units of travel. The old choreography moved 92 units on a 717-wide
 *  viewBox — ~11px at the rendered size. Scaled to this viewBox's width
 *  (172.36 / 717) that is 22 units, the same travel on screen. */
const SPREAD = 22;

/* ============================================================
   State
   ============================================================ */

let CFG = { ...DEFAULTS };
let bound = false;

let veil = null;
let rack = null;
let shards = [];
let mark = null;
let limbL = null;
let limbR = null;
let outline = null;

/* (the old `master` GSAP timeline is gone — see `anims` and `t0` at the head
   of this file. There is no timeline object any more; the choreography is a
   set of delayed WAAPI animations against one recorded start time.) */
let pending = null;      // href we owe a navigation to
let navigating = false;
let safetyTimer = 0;
let capTimer = 0;

let wideMql = null;

/* ============================================================
   Capability gates
   ============================================================ */

/**
 * Has the user seen content yet? If yes we must not cover it.
 * Deliberately fails closed: anything unknown counts as painted, so the
 * worst case is no entrance animation rather than a page blacked out
 * after the reader already had it.
 */
function contentPainted() {
  try {
    const types = window.PerformanceObserver && PerformanceObserver.supportedEntryTypes;
    if (!types || !types.includes('paint')) return true;
    return performance
      .getEntriesByType('paint')
      .some((e) => e.name === 'first-contentful-paint');
  } catch {
    return true;
  }
}

/**
 * Two cores or two gigabytes. Shopify's own view-transitions.js draws the
 * line in exactly the same place, and a full-viewport composite is a poor
 * thing to spend a weak device's frame budget on.
 */
function lowEnd() {
  const cores = navigator.hardwareConcurrency;
  const mem = navigator.deviceMemory;
  return (cores > 0 && cores <= 2) || (mem > 0 && mem <= 2);
}

/** Every reason to leave the browser alone, in one place. */
function suppressed() {
  return reduced() || lowEnd();
}

function shardCount() {
  if (typeof CFG.shards === 'number') return Math.max(1, Math.round(CFG.shards));
  if (!wideMql) wideMql = window.matchMedia('(min-width: 44em)');
  return wideMql.matches ? 9 : 5;
}

/* ============================================================
   The veil
   ============================================================ */

function build() {
  if (veil) return veil;

  const n = shardCount();

  veil = document.createElement('div');
  veil.className = 'aep-veil';
  veil.hidden = true;
  veil.setAttribute('aria-hidden', 'true');
  veil.setAttribute('inert', '');
  veil.style.setProperty('--aep-n', String(n));

  rack = document.createElement('div');
  rack.className = 'aep-veil__rack';
  for (let i = 0; i < n; i++) {
    const shard = document.createElement('i');
    shard.className = 'aep-veil__shard';
    rack.appendChild(shard);
  }

  veil.appendChild(rack);
  veil.insertAdjacentHTML('beforeend', MARK);
  (document.body || document.documentElement).appendChild(veil);

  shards = Array.from(rack.children);
  mark = veil.querySelector('.aep-veil__mark');
  limbL = veil.querySelector('[data-limb="l"]');
  limbR = veil.querySelector('[data-limb="r"]');
  outline = veil.querySelector('.aep-veil__outline');

  let len = MARK_LENGTH;
  try {
    const measured = outline.getTotalLength();
    if (measured > 1) len = measured;
  } catch { /* keep the hand-computed perimeter */ }
  outline.style.strokeDasharray = String(len);
  outline.dataset.len = String(len);

  return veil;
}

/** Rebuild at the other shard count when the viewport crosses 44em. */
function watchBreakpoint() {
  if (typeof CFG.shards === 'number') return;
  if (!wideMql) wideMql = window.matchMedia('(min-width: 44em)');
  wideMql.addEventListener('change', () => {
    if (navigating || !veil) return;
    veil.remove();
    veil = rack = mark = limbL = limbR = outline = null;
    shards = [];
  });
}

function show() {
  veil.hidden = false;
  veil.classList.add('is-active');
}

/** The mark, complete and at rest. Also what any snapshot of us should show. */
function seatMark() {
  killAll();
  setStyle(mark, { transform: 'none', opacity: '1' });
  setStyle(limbL, { transform: 'none', opacity: '1' });
  setStyle(limbR, { transform: 'none', opacity: '1' });
  setStyle(outline, { opacity: '0', strokeDashoffset: '0' });
}

/**
 * How far the mark got before we left, 0..1.
 *
 * The exit keeps assembling the mark while the network works, so how much of
 * it the reader actually saw is only known at the very last moment. Recording
 * it lets the arriving page continue from the same image instead of popping a
 * finished mark over a half-drawn one — the two documents cannot share a
 * timeline, so they share a number.
 */
function markProgress() {
  if (!t0) return 0;
  const t = (performance.now() - t0) / 1000;
  return Math.min(1, Math.max(0, (t - T.markIn) / (MARK_DONE - T.markIn)));
}

/** The mark, dismantled: limbs out along their own axis, outline undrawn.
 *
 *  The limb offsets are SVG USER UNITS, not CSS pixels — the viewBox is
 *  `0 0 717 743`, and a `px` length in a CSS transform on an SVG child
 *  resolves in the local user coordinate system. */
function primeMark() {
  killAll();
  const len = Number(outline.dataset.len) || MARK_LENGTH;
  setStyle(mark, { transform: 'none', opacity: '1' });
  setStyle(limbL, { transform: `translate(${-AXIS.x * SPREAD}px, ${AXIS.y * SPREAD}px)`, opacity: '0' });
  setStyle(limbR, { transform: `translate(${AXIS.x * SPREAD}px, ${AXIS.y * SPREAD}px)`, opacity: '0' });
  setStyle(outline, { opacity: '0.55', strokeDashoffset: String(len) });
}

/**
 * Put the veil away and forget everything. Safe to call at any moment,
 * from any handler, however many times. This is the function that makes
 * "never trap the user" true.
 */
function hardClear() {
  clearTimeout(safetyTimer);
  clearTimeout(capTimer);
  safetyTimer = 0;
  capTimer = 0;
  pending = null;
  navigating = false;
  t0 = 0;

  killAll();
  if (!veil) return;

  veil.classList.remove('is-active');
  veil.hidden = true;
}

function armSafety(ms) {
  clearTimeout(safetyTimer);
  safetyTimer = setTimeout(hardClear, ms || CFG.safetyMs);
}

/* ============================================================
   Exit — the field closing
   ============================================================ */

/**
 * Cover the viewport, then navigate. Exported so a page can hand off
 * deliberately (after an add-to-cart, say) and get the same gesture.
 * @param {string} href same-origin URL
 */
export function leaveTo(href) {
  if (navigating) return;
  if (suppressed()) {
    window.location.assign(href);
    return;
  }

  navigating = true;
  pending = href;

  // Armed first, before a single line of animation code can throw. If
  // everything below fails, the veil still comes off on its own.
  armSafety();
  capTimer = setTimeout(commit, CFG.maxSealMs);

  try {
    build();
    primeMark();                                    // also kills anything running
    shards.forEach((s) => setStyle(s, { transform: 'translate3d(0, 100%, 0)' }));
    show();

    const len = Number(outline.dataset.len) || MARK_LENGTH;
    const n = shards.length;
    t0 = performance.now();

    // The rack. Centre leads, so for a couple of hundred milliseconds the
    // silhouette is a stepped peak — the mark drawn in negative space by
    // the thing covering the page — and then the steps close.
    let sealLast = null;
    let sealLastDelay = -1;
    shards.forEach((shard, i) => {
      const delay = CENTRE_DELAY(i, n, T.sealStagger);
      const a = anim(shard,
        [{ transform: 'translate3d(0, 100%, 0)' }, { transform: 'translate3d(0, 0, 0)' }],
        { duration: ms(T.seal), delay: ms(delay) });
      if (delay > sealLastDelay) { sealLastDelay = delay; sealLast = a; }
    });
    // The moment it is opaque, we go. GSAP fired this from the stagger's
    // onComplete, i.e. when the LAST instance landed — which is the
    // outermost shard, the one with the largest centre-out delay.
    if (sealLast) sealLast.finished.then(commit, () => { /* cancelled: commit() already ran or hardClear did */ });

    // Outline traced from the apex clockwise; the last thing it does is
    // climb the left edge and arrive back at the peak.
    anim(outline,
      [{ strokeDashoffset: len }, { strokeDashoffset: 0 }],
      { duration: ms(T.draw), delay: ms(T.markIn) });

    // The limbs converge along their own longest edge. Both are travelling up.
    [limbL, limbR].forEach((limb, i) => {
      const from = i === 0
        ? `translate(${-AXIS.x * SPREAD}px, ${AXIS.y * SPREAD}px)`
        : `translate(${AXIS.x * SPREAD}px, ${AXIS.y * SPREAD}px)`;
      anim(limb,
        [{ transform: from, opacity: 0 }, { transform: 'translate(0px, 0px)', opacity: 1 }],
        { duration: ms(T.facet), delay: ms(T.markIn + 0.06 + i * 0.05) });
    });

    // The outline retires as the fill arrives inside it: one crossing, no flash.
    anim(outline,
      [{ opacity: 0.55 }, { opacity: 0 }],
      { duration: ms(0.32), delay: ms(T.markIn + T.draw - 0.08) });

    // Sustain. Reached only if the network is slow enough that stillness
    // would read as a hang — the assembled mark, breathing, very slowly,
    // over two seconds. Not a spinner, not a progress bar, and nothing
    // that implies a percentage we do not know.
    anim(mark,
      [{ opacity: 1 }, { opacity: 0.55 }],
      {
        duration: ms(T.breath),
        delay: ms(T.breathAt),
        easing: 'linear',
        direction: 'alternate',
        iterations: Infinity,
      });
  } catch (err) {
    // Any failure at all means "no transition", never "no page".
    if (typeof console !== 'undefined') console.warn('[aeperion] transition failed:', err);
    hardClear();
    window.location.assign(href);
  }
}

/** Hand over to the browser. Idempotent — whichever of the two callers wins. */
function commit() {
  if (!pending) return;
  const href = pending;
  pending = null;
  clearTimeout(capTimer);
  capTimer = 0;

  // The next page claims this to know the entrance is owed. sessionStorage
  // is per-tab, which is the correct scope: a cmd-click opens a new tab and
  // is never intercepted in the first place, so it can never inherit a stamp.
  try { sessionStorage.setItem(FLAG, String(Date.now())); } catch { /* private mode */ }

  try {
    window.location.assign(href);
  } catch {
    window.location.href = href;
  }
}

/* ============================================================
   Enter — the field leaving
   ============================================================ */

function playEnter() {
  let stamp = 0;
  try {
    stamp = Number(sessionStorage.getItem(FLAG)) || 0;
    sessionStorage.removeItem(FLAG);     // consume it whatever happens next
  } catch { /* private mode: no entrance, no harm */ }

  if (!stamp) return;                                    // not our navigation
  if (suppressed()) return;
  if (Date.now() - stamp > CFG.flagTtlMs) return;        // stale stamp

  const nav = performance.getEntriesByType?.('navigation')?.[0];
  if (nav && nav.type === 'back_forward') return;        // history, not a click

  // The load-bearing check. If the reader can already see this page, the
  // only decent thing to do is nothing.
  if (contentPainted()) return;

  try {
    build();
    seatMark();                                     // also kills anything running
    shards.forEach((s) => setStyle(s, { transform: 'translate3d(0, 0, 0)' }));
    show();
    armSafety(2500);

    const n = shards.length;
    t0 = performance.now();

    // The mark lets go upward — it leaves the way it arrived.
    anim(mark,
      [{ transform: 'translate(0px, 0px)', opacity: 1 }, { transform: 'translate(0px, -10px)', opacity: 0 }],
      { duration: ms(T.release) });

    // Same rack, same direction, still ascending. Centre leads again, so
    // the middle of the page is legible first.
    let last = null;
    let lastDelay = -1;
    shards.forEach((shard, i) => {
      const delay = 0.08 + CENTRE_DELAY(i, n, T.clearStagger);
      const a = anim(shard,
        [{ transform: 'translate3d(0, 0, 0)' }, { transform: 'translate3d(0, -100%, 0)' }],
        { duration: ms(T.clear), delay: ms(delay) });
      if (delay > lastDelay) { lastDelay = delay; last = a; }
    });
    // GSAP's timeline onComplete. The rack is the longest strand, so the
    // last shard off stage is the end of the entrance.
    if (last) last.finished.then(hardClear, () => { /* cancelled; hardClear already ran */ });
  } catch (err) {
    if (typeof console !== 'undefined') console.warn('[aeperion] entrance failed:', err);
    hardClear();
  }
}

/* ============================================================
   Link interception
   ============================================================ */

const NON_DOCUMENT = /\.(pdf|zip|rar|7z|dmg|exe|csv|txt|xml|jpe?g|png|gif|webp|avif|svg|mp4|webm|mov|mp3|wav)$/i;

function anchorFrom(event) {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : null;
  if (path) {
    // composedPath, not closest: the theme is full of web components and a
    // link inside a shadow root is still a link.
    for (const node of path) {
      if (node instanceof HTMLAnchorElement) return node;
      if (node === document) break;
    }
    return null;
  }
  return event.target && event.target.closest ? event.target.closest('a[href]') : null;
}

/**
 * @returns {URL|null} the destination, or null if this link is none of our business.
 */
function eligible(a) {
  if (!a || !a.hasAttribute('href')) return null;
  if (a.hasAttribute('download')) return null;

  const target = (a.getAttribute('target') || '').toLowerCase();
  if (target && target !== '_self') return null;

  const rel = (a.getAttribute('rel') || '').toLowerCase();
  if (rel.split(/\s+/).includes('external')) return null;

  if (CFG.ignore && (a.matches(CFG.ignore) || a.closest(CFG.ignore))) return null;

  const raw = a.getAttribute('href') || '';
  if (!raw || raw.charAt(0) === '#') return null;

  let url;
  try {
    url = new URL(a.href, window.location.href);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin) return null;       // same-origin only
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  if (NON_DOCUMENT.test(url.pathname)) return null;             // a file, not a page

  // Same document, different hash: that is in-page anchoring. Leave it to
  // the browser, which does it better and does not need covering.
  if (url.pathname === window.location.pathname && url.search === window.location.search) {
    return null;
  }

  return url;
}

function onClick(event) {
  if (event.defaultPrevented) return;              // a theme handler got here first
  if (event.button !== 0) return;                  // middle, right
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (navigating) return;
  if (suppressed()) return;                        // re-checked per click: a
                                                   // mid-session settings change
                                                   // takes effect immediately

  const a = anchorFrom(event);
  const url = eligible(a);
  if (!url) return;

  event.preventDefault();
  leaveTo(url.href);
}

/* ============================================================
   Warming

   Not decoration: it is what makes the 400ms seal cost nothing. If the
   theme already ships speculation rules — Tinker does, eagerness
   moderate — we stay out of its way entirely.
   ============================================================ */

function initPrefetch() {
  let supports = false;
  try {
    supports = document.createElement('link').relList.supports('prefetch');
  } catch { /* older engines */ }
  if (!supports) return;
  if (document.querySelector('script[type="speculationrules"]')) return;

  const conn = navigator.connection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;

  const warmed = new Set();

  const warm = (event) => {
    const a = anchorFrom(event) || (event.target && event.target.closest
      ? event.target.closest('a[href]')
      : null);
    const url = eligible(a);
    if (!url || warmed.has(url.href)) return;
    warmed.add(url.href);

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'document';
    link.href = url.href;
    document.head.appendChild(link);
  };

  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('focusin', warm, { passive: true });
}

/* ============================================================
   Lifecycle

   The veil must not survive an event it was not designed for. Every
   handler below exists because of one specific way an overlay gets
   stuck on screen.
   ============================================================ */

/**
 * Opt the document out of cross-document view transitions. Appended to
 * <head> so it is the last @view-transition rule the cascade sees, which is
 * what the spec keys off. Same technique as Shopify's view-transitions.js.
 */
function disableViewTransitions() {
  if (!('CSSViewTransitionRule' in window)) return;   // nothing to disable
  const style = document.createElement('style');
  style.dataset.aepVt = 'off';
  style.textContent = '@view-transition { navigation: none; }';
  document.head.appendChild(style);
}

/** Cross-document view transition, if the document has one: fires before the
 *  outgoing snapshot is taken, so this is the last chance to make that
 *  snapshot show a finished mark rather than a half-drawn one. */
function onPageSwap() {
  if (!veil || veil.hidden) return;

  // Hand the mark's state to the next document before killing the timeline.
  // markProgress() reads wall time, so it is still correct here — and it stays
  // correct after killAll(), which is why it no longer reads a timeline object.
  try { sessionStorage.setItem(FLAG_MARK, markProgress().toFixed(3)); } catch { /* private mode */ }

  seatMark();                                       // kills, then seats
  shards.forEach((s) => setStyle(s, { transform: 'translate3d(0, 0, 0)' }));
}

/** Fires on every unload, including into bfcache. Same job, no VT required. */
function onPageHide() {
  clearTimeout(safetyTimer);
  clearTimeout(capTimer);
  onPageSwap();
}

/** Coming back from bfcache restores the DOM exactly as it was — including a
 *  sealed veil. Clear it synchronously, before the restored frame is painted. */
function onPageShow(event) {
  if (!event.persisted) return;
  hardClear();
  requestAnimationFrame(hardClear);   // belt to the brace
}

/* ============================================================
   Boot
   ============================================================ */

const controller = {
  /** Unbind everything and remove the veil. Leaves the page fully navigable. */
  destroy() {
    if (!bound) return;
    bound = false;
    document.removeEventListener('click', onClick);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('popstate', hardClear);
    window.removeEventListener('pageswap', onPageSwap);
    hardClear();
    if (veil) {
      veil.remove();
      veil = rack = mark = limbL = limbR = outline = null;
      shards = [];
    }
  },
  leaveTo,
};

/**
 * @param {object}  [options]
 * @param {number|'auto'} [options.shards='auto']   shards across
 * @param {boolean} [options.prefetch=true]         warm links on hover/focus
 * @param {string}  [options.ignore]                selector that opts links out
 * @param {'cooperate'|'replace'} [options.viewTransitions='cooperate']
 * @param {number}  [options.maxSealMs=520]         hard cap on added latency
 * @param {number}  [options.safetyMs=6000]         unconditional release
 * @returns {{destroy: () => void, leaveTo: (href: string) => void}}
 */
export function initTransitions(options) {
  if (bound) return controller;
  bound = true;
  CFG = { ...DEFAULTS, ...(options || {}) };

  // Handlers bind even under reduced motion / low-end, and check on every
  // click instead. It costs one listener and means a settings change mid
  // session is honoured without a reload.
  document.addEventListener('click', onClick);          // bubble, not capture:
                                                        // theme handlers get
                                                        // first refusal
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('popstate', hardClear);
  if ('onpageswap' in window) window.addEventListener('pageswap', onPageSwap);

  watchBreakpoint();
  if (CFG.prefetch) initPrefetch();
  if (CFG.viewTransitions === 'replace') disableViewTransitions();

  // A prerendered page runs its scripts long before anyone looks at it, and
  // its paint timings are measured from activation. Wait for the real thing.
  if (document.prerendering) {
    document.addEventListener('prerenderingchange', () => playEnter(), { once: true });
  } else {
    playEnter();
  }

  return controller;
}

export default initTransitions;
